import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Musikk, MusikkKategori, musikkAPI } from '@/services/api';
import { useEditSongForm } from './edit-song-tab/hooks/useEditSongForm';
import { useCategoryManagement } from './edit-song-tab/hooks/useCategoryManagement';
import { SongBasicInfoCard } from './edit-song-tab/components/SongBasicInfoCard';
import { SongTextCard } from './edit-song-tab/components/SongTextCard';

export interface EditSongFormData {
  tittel: string;
  artist: string;
  album: string;
  utgivelsesaar: number | '';
  bpm: number | '';
  takt: string;
  lengde_minutter: number | '';
  lengde_sekunder: number | '';
  grunntone: string;
  toneart_type: string;
  categories: string[];
  tekst_og_akkorder: string;
  notater: string;
  publisert: boolean;
}

interface Props {
  musikk: Musikk | null;
  onSave: () => void;
}

export interface EditSongTabRef {
  getFormData: () => EditSongFormData;
  validate: () => { isValid: boolean; errors: string[] };
  reloadKategorier: () => Promise<void>;
}

const EditSongTab = forwardRef<EditSongTabRef, Props>(({ musikk, onSave }, ref) => {
  const { formData, updateField, validate: validateForm } = useEditSongForm({ musikk });
  
  const [allKategorier, setAllKategorier] = useState<MusikkKategori[]>([]);

  useEffect(() => {
    loadKategorier();
  }, []);

  const loadKategorier = async () => {
    try {
      const response = await musikkAPI.getAllKategorier();
      setAllKategorier(response.kategorier);
    } catch (err) {
      console.error('Kunne ikke laste kategorier:', err);
    }
  };

  const categoryManagement = useCategoryManagement({
    kategorier: allKategorier,
    selectedCategories: formData.categories,
    onSelectedCategoriesChange: (categories) => updateField('categories', categories),
  });

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getFormData: (): EditSongFormData => ({
      tittel: formData.tittel,
      artist: formData.artist,
      album: formData.album,
      utgivelsesaar: formData.utgivelsesaar,
      bpm: formData.bpm,
      takt: formData.takt,
      lengde_minutter: formData.lengdeMinutter,
      lengde_sekunder: formData.lengdeSekunder,
      grunntone: formData.grunntone,
      toneart_type: formData.toneartType,
      categories: formData.categories,
      tekst_og_akkorder: formData.tekstOgAkkorder,
      notater: formData.notater,
      publisert: formData.publisert,
    }),
    reloadKategorier: async () => {
      await loadKategorier();
    },
    validate: validateForm,
  }));

  return (
    <div className="space-y-6">
      <SongBasicInfoCard
        tittel={formData.tittel}
        artist={formData.artist}
        album={formData.album}
        utgivelsesaar={formData.utgivelsesaar}
        bpm={formData.bpm}
        takt={formData.takt}
        lengdeMinutter={formData.lengdeMinutter}
        lengdeSekunder={formData.lengdeSekunder}
        categories={formData.categories}
        categoryInput={categoryManagement.categoryInput}
        showKategoriDropdown={categoryManagement.showKategoriDropdown}
        newKategoriWarning={categoryManagement.newKategoriWarning}
        filteredKategorier={categoryManagement.filteredKategorier}
        notater={formData.notater}
        publisert={formData.publisert}
        onTittelChange={(value) => updateField('tittel', value)}
        onArtistChange={(value) => updateField('artist', value)}
        onAlbumChange={(value) => updateField('album', value)}
        onUtgivelsesaarChange={(value) => updateField('utgivelsesaar', value)}
        onBpmChange={(value) => updateField('bpm', value)}
        onTaktChange={(value) => updateField('takt', value)}
        onLengdeMinutterChange={(value) => updateField('lengdeMinutter', value)}
        onLengdeSekunderChange={(value) => updateField('lengdeSekunder', value)}
        onCategoriesChange={(categories) => updateField('categories', categories)}
        onCategoryInputChange={categoryManagement.handleCategoryInputChange}
        onCategoryInputKeyDown={categoryManagement.handleAddCategory}
        onCategoryInputFocus={() => {
          if (categoryManagement.categoryInput.trim().length > 0) {
            categoryManagement.setShowKategoriDropdown(true);
          }
        }}
        onCategoryInputBlur={() => {
          setTimeout(() => categoryManagement.setShowKategoriDropdown(false), 200);
        }}
        onCategorySelect={categoryManagement.handleCategorySelect}
        onConfirmNewCategory={categoryManagement.confirmNewCategory}
        onRemoveCategory={categoryManagement.removeCategory}
        onCancelNewCategory={() => {
          categoryManagement.setCategoryInput('');
          categoryManagement.setNewKategoriWarning(null);
        }}
        onNotaterChange={(value) => updateField('notater', value)}
        onPublisertToggle={() => updateField('publisert', !formData.publisert)}
      />

      <SongTextCard
        grunntone={formData.grunntone}
        toneartType={formData.toneartType}
        tekstOgAkkorder={formData.tekstOgAkkorder}
        onGrunntoneChange={(value) => updateField('grunntone', value)}
        onToneartTypeChange={(value) => updateField('toneartType', value)}
        onTekstOgAkkorderChange={(value) => updateField('tekstOgAkkorder', value)}
      />
    </div>
  );
});

EditSongTab.displayName = 'EditSongTab';

export default EditSongTab;

